import { Controller } from "@hotwired/stimulus";
import { dsConfirm } from "../utils/ds_confirm";

export default class extends Controller {
  static targets = [
    "reportsList",
    "modal",
    "modalTitle",
    "reportForm",
    "reportId",
    "nameInput",
    "frequencySelect",
    "formatSelect",
    "recipientsInput",
    "submitText",
  ];
  static values = { schoolId: Number };

  connect() {
    this.isEditing = false;
  }

  showCreateForm() {
    this.isEditing = false;
    this.modalTitleTarget.textContent = this.t(
      "js.rh.teacher_attendances.reports.modal.create_title",
    );
    this.submitTextTarget.textContent = this.t(
      "js.rh.teacher_attendances.reports.modal.create_submit",
    );
    this.reportFormTarget.reset();
    this.reportIdTarget.value = "";
    this.showModal();
  }

  showEditForm(event) {
    const reportId = event.currentTarget.dataset.reportId;
    this.isEditing = true;
    this.modalTitleTarget.textContent = this.t(
      "js.rh.teacher_attendances.reports.modal.edit_title",
    );
    this.submitTextTarget.textContent = this.t(
      "js.rh.teacher_attendances.reports.modal.edit_submit",
    );

    // Récupérer les données du rapport depuis la ligne du tableau
    const row = event.currentTarget.closest("tr");
    const name = row.querySelector("td:nth-child(1)").textContent.trim();
    const frequency = this.getFrequencyFromRow(row);
    const format = this.getFormatFromRow(row);

    this.reportIdTarget.value = reportId;
    this.nameInputTarget.value = name;
    this.frequencySelectTarget.value = frequency;
    this.formatSelectTarget.value = format;

    this.showModal();
  }

  getFrequencyFromRow(row) {
    const frequencyText = row
      .querySelector("td:nth-child(2)")
      .textContent.trim()
      .toLowerCase();

    const mappings = {
      daily: this.t("js.rh.teacher_attendances.reports.frequencies.daily"),
      weekly: this.t("js.rh.teacher_attendances.reports.frequencies.weekly"),
      monthly: this.t("js.rh.teacher_attendances.reports.frequencies.monthly"),
    };

    for (const [key, label] of Object.entries(mappings)) {
      if (label && frequencyText.includes(label.toLowerCase())) {
        return key;
      }
    }

    return "custom";
  }

  getFormatFromRow(row) {
    const formatText = row
      .querySelector("td:nth-child(3)")
      .textContent.trim()
      .toLowerCase();
    if (formatText.includes("csv")) return "csv";
    if (formatText.includes("pdf")) return "pdf";
    if (formatText.includes("xlsx") || formatText.includes("excel"))
      return "xlsx";
    return "csv";
  }

  showModal() {
    this.modalTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    this.modalTarget.classList.add("hidden");
    document.body.style.overflow = "auto";
    this.reportFormTarget.reset();
  }

  async submitForm(event) {
    event.preventDefault();

    const formData = new FormData(this.reportFormTarget);
    const reportId = this.reportIdTarget.value;

    const url = this.isEditing
      ? `/schools/rh/teacher_attendance_report_configs/${reportId}`
      : "/schools/rh/teacher_attendances/report_configs";

    const method = this.isEditing ? "PATCH" : "POST";

    try {
      const response = await fetch(url, {
        method: method,
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "application/json",
        },
      });

      const result = await response.json();

      if (response.ok) {
        const successKey = this.isEditing
          ? "js.rh.teacher_attendances.reports.notifications.update_success"
          : "js.rh.teacher_attendances.reports.notifications.create_success";
        this.showNotification(this.t(successKey), "success");
        this.closeModal();
        this.refreshReportsList();
      } else {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.reports.notifications.save_error",
            ),
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t("js.rh.teacher_attendances.reports.notifications.generic_error"),
        "error",
      );
    }
  }

  async deleteReport(event) {
    const reportId = event.currentTarget.dataset.reportId;
    const row = event.currentTarget.closest("tr");
    const reportName = row.querySelector("td:nth-child(1)").textContent.trim();

    const confirmed = await dsConfirm(
      this.t(
        "js.rh.teacher_attendances.reports.notifications.delete_confirm",
        { name: reportName },
      ),
      { tone: "danger" },
    );
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(
        `/schools/rh/teacher_attendance_report_configs/${reportId}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
            Accept: "application/json",
          },
        },
      );

      const result = await response.json();

      if (response.ok) {
        this.showNotification(
          this.t(
            "js.rh.teacher_attendances.reports.notifications.delete_success",
          ),
          "success",
        );
        this.refreshReportsList();
      } else {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.reports.notifications.delete_error",
            ),
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.reports.notifications.delete_generic_error",
        ),
        "error",
      );
    }
  }

  async downloadExport(event) {
    const exportId = event.currentTarget.dataset.exportId;

    try {
      // Simuler le téléchargement
      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.reports.notifications.download_start",
        ),
        "info",
      );

      // Créer un fichier de démonstration
      const csvContent = this.t(
        "js.rh.teacher_attendances.reports.export.sample_csv",
        { date: new Date().toLocaleDateString() },
      );

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filenamePrefix = this.t(
        "js.rh.teacher_attendances.reports.export.filename_prefix",
      );
      a.download = `${filenamePrefix}_${exportId}_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.reports.notifications.download_success",
        ),
        "success",
      );
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.reports.notifications.download_error",
        ),
        "error",
      );
    }
  }

  showHistory() {
    // Afficher l'historique des exports
    this.showNotification(
      this.t("js.rh.teacher_attendances.reports.notifications.history_wip"),
      "info",
    );
  }

  async refreshReportsList() {
    try {
      const response = await fetch("/schools/rh/teacher_attendances/reports", {
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        // Recharger la page pour mettre à jour les données
        window.location.reload();
      }
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
    }
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    return key;
  }

  showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-md shadow-lg z-50 ${
      type === "success"
        ? "bg-cta text-white"
        : type === "error"
          ? "bg-red-500 text-white"
          : "bg-blue-500 text-white"
    }`;
    notification.textContent = message;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}
